import React, { useContext } from "react";

import { FormHelperText, Grid, makeStyles } from "@material-ui/core";
import { ErrorMessage, Form, Formik } from "formik";
import * as yup from "yup";

import InputLabel from "../../../components/Form/InputLabel";
import FormLabel from "../../../components/Form/FormLabel";
import TextField from "../../../components/Form/TextField";
import DialogActions from "../../../components/Form/DialogActions";
import { HospitalsContext } from "../RodaHomepage";
import { HospitalInfoContext } from "./HospitalInfoTable";
import { saveHospitalInfo } from "../../../api/hospital";
import {
  invalidAddressLength,
  invalidEmail,
  invalidHospitalForm,
  invalidNameLength,
  invalidPhoneLength,
  requiredField,
} from "../../../constants/messages";
import theme from "../../../constants/theme";

const useStyles = makeStyles({
  error: {
    color: theme.palette.error.main,
  },
  required: {
    color: "#992468",
    fontSize: 24,
  },
  marginNone: {
    margin: 0,
  },
});

const hospitalInfoSchema = yup.object().shape({
  name: yup.string().max(80, invalidNameLength).required(requiredField),
  streetAddress: yup
    .string()
    .max(50, invalidAddressLength)
    .required(requiredField),
  phone1: yup.string().max(15, invalidPhoneLength).required(requiredField),
  phone2: yup.string().max(15, invalidPhoneLength).notRequired().nullable(),
  email: yup.string().email(invalidEmail).notRequired().nullable(),
});

type HospitalInfoFormProps = {
  handleClose: () => void;
};

export default function HospitalInfoForm({
  handleClose,
}: HospitalInfoFormProps) {
  const classes = useStyles();
  const { currentHospital } = useContext(HospitalsContext);
  const { hospitalInfo, setHospitalInfo } = useContext(HospitalInfoContext);

  return (
    <Formik
      initialValues={hospitalInfo}
      validationSchema={hospitalInfoSchema}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={async (values) => {
        handleClose();
        const response = await saveHospitalInfo(currentHospital, values);
        setHospitalInfo(response.data);
      }}
    >
      {({ values, handleChange, handleSubmit, errors }) => (
        <Form>
          <FormLabel>Podaci o ustanovi</FormLabel>
          <InputLabel>Ime ustanove:</InputLabel>
          <TextField
            autoFocus
            onChange={handleChange}
            value={values.name}
            name={"name"}
            fullWidth
          />
          <ErrorMessage
            name={"name"}
            component={FormHelperText}
            className={classes.error}
          />
          <InputLabel>Regija:</InputLabel>
          <TextField
            disabled
            value={values.cityRegionName}
            name={"region"}
            fullWidth
          />
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <InputLabel>Adresa:</InputLabel>
              <TextField
                onChange={handleChange}
                value={values.streetAddress}
                name={"streetAddress"}
                fullWidth
              />
              <ErrorMessage
                name={"streetAddress"}
                component={FormHelperText}
                className={classes.error}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel>Grad:</InputLabel>
              <TextField
                disabled
                value={`${values.cityName} ${values.cityPostcode}`}
                name={"cityName"}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel>Broj telefona 1:</InputLabel>
              <TextField
                onChange={handleChange}
                value={values.phone1}
                name={"phone1"}
                fullWidth
              />
              <ErrorMessage
                name={"phone1"}
                component={FormHelperText}
                className={classes.error}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel>Broj telefona 2:</InputLabel>
              <TextField
                onChange={handleChange}
                value={values.phone2 || ""}
                name={"phone2"}
                fullWidth
              />
              <ErrorMessage
                name={"phone2"}
                component={FormHelperText}
                className={classes.error}
              />
            </Grid>
          </Grid>
          <InputLabel>Link na web stranicu:</InputLabel>
          <TextField
            onChange={handleChange}
            value={values.website || ""}
            name={"website"}
            fullWidth
          />
          <InputLabel>Email:</InputLabel>
          <TextField
            type="email"
            onChange={handleChange}
            value={values.email || ""}
            name={"email"}
            fullWidth
          />
          <ErrorMessage
            name={"email"}
            component={FormHelperText}
            className={classes.error}
          />
          <DialogActions
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            message={
              (errors.name || errors.streetAddress || errors.phone1) &&
              invalidHospitalForm
            }
          />
        </Form>
      )}
    </Formik>
  );
}
